import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import EmptyState from '@atlaskit/empty-state/empty-state';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import Button from '@atlaskit/button/default/button';

import GenericErrorImage from './assets/GenericErrorImage.svg';

const i18n = defineMessages({
	errorStateHeaderTitle: {
		id: 'version-history.error-state.header.title',
		defaultMessage: 'Unable to load',
		description:
			'The text is shown as a heading in an empty state panel within the version history side panel when the version history data fails to load.',
	},
	newRetryOrRefreshText: {
		id: 'version-history.error-state.new.retry.or.refresh.text',
		defaultMessage: 'We were unable to load history. Try again, or check your network connection.',
		description: 'Generalized text indicating issue with loading data and to retry or refresh page',
	},
	retryButtonText: {
		id: 'version-history.error-state.retry.button.text',
		defaultMessage: 'Retry',
		description:
			'The text is shown on a primary button in the version history error state panel. When clicked, it retries loading the version history data.',
	},
});

const errorStateContainerStyles = xcss({
	flexGrow: 1,
});

type ErrorStateProps = {
	refetch: () => void;
};

export const ErrorState = ({ refetch }: ErrorStateProps) => {
	const { formatMessage } = useIntl();

	const handleRefetch = useCallback(() => {
		void refetch();
	}, [refetch]);

	return (
		<Box
			xcss={errorStateContainerStyles}
			paddingInline="space.600"
			paddingBlockEnd="space.600"
			testId="version-error-state"
		>
			<EmptyState
				header={formatMessage(i18n.errorStateHeaderTitle)}
				description={formatMessage(i18n.newRetryOrRefreshText)}
				primaryAction={
					<Button appearance="primary" onClick={handleRefetch}>
						{formatMessage(i18n.retryButtonText)}
					</Button>
				}
				imageUrl={GenericErrorImage}
			/>
		</Box>
	);
};
